import { useEffect } from 'react';
import { startOfToday } from 'date-fns';
import { useDispatch, useSelector } from './reactRedux';
import { sortBy, uniqBy } from 'lodash';

import type { Interval, PlanGroup, PlanUnit } from 'components/types';

import { type PlanUnitType } from 'components/enums';

import { sortAlphabeticallyBy } from 'utils/misc';
import { isValidPlanUnit } from 'components/shared/PlanUnitSelector/util';
import { fetchPlanUnits } from '../redux/actionCreators';

type UseOptionsProps = {
  showGroups: boolean;
  useEmployeePlanunits?: boolean;
  usePublicPlanUnits?: boolean;
  interval?: Interval;
  filterByType?: PlanUnitType;
};

export const usePlanUnitsOptions = ({
  showGroups = true,
  useEmployeePlanunits,
  usePublicPlanUnits,
  interval: intervalProp,
  filterByType
}: UseOptionsProps) => {
  'use memo';
  const interval = intervalProp || {
    startDate: startOfToday(),
    endDate: startOfToday()
  };
  const startTime = interval.startDate.getTime();
  const endTime = interval.endDate.getTime();

  const dispatch = useDispatch();
  const planGroups = useSelector((state) => state.planGroups);

  const planUnitKey = useEmployeePlanunits
    ? 'employeePlanUnits'
    : usePublicPlanUnits
    ? 'publicPlanUnits'
    : 'planUnits';

  const planUnits = useSelector((state) => state[planUnitKey]);

  useEffect(() => {
    dispatch(
      fetchPlanUnits({
        interval: {
          startDate: new Date(startTime),
          endDate: new Date(endTime)
        },
        useEmployeePlanunits,
        usePublicPlanUnits,
        filterByType
      })
    );
    if (showGroups) {
      dispatch({ type: 'FETCH_PLANGROUPS' });
    }
  }, [
    dispatch,
    showGroups,
    useEmployeePlanunits,
    usePublicPlanUnits,
    filterByType,
    startTime,
    endTime
  ]);

  const planGroupOptions: PlanGroup[] = showGroups
    ? sortAlphabeticallyBy(planGroups, 'name')
    : [];
  const uniqueValidPlanUnits: PlanUnit[] = uniqBy(
    sortBy(planUnits, (o: PlanUnit) =>
      isValidPlanUnit(o.validity, interval)
    ).reverse(),
    'id'
  );

  return [
    ...planGroupOptions,
    ...sortAlphabeticallyBy(uniqueValidPlanUnits, 'name')
  ] as (PlanUnit | PlanGroup)[];
};
